import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useWhoAmI, type WhoAmIShop } from './WhoAmIContext'

// Shop context — the currently-selected shop that Orders + Earnings screens
// operate on. Reads the merchant's shop list from WhoAmIContext (which
// serves the unified /v1/whoami identity), auto-selects when there's
// exactly one, otherwise sets `status: 'needs-picker'` so the app can
// render the picker screen.
//
// Persistence: selected shop handle lives in localStorage so re-launches
// don't re-prompt every time. On boot, if the persisted handle is in the
// fresh shop list, it wins. Otherwise auto-select or picker as above.
//
// Split from WhoAmIContext because "which shop is selected" is a UI
// concern (localStorage + user click), while "what does whoami say" is
// pure server data. Keeps the two lifecycle concerns separate.

const STORAGE_KEY = 'headlo-mobile:selected-shop'

export type Shop = WhoAmIShop

export type ShopStatus =
  | { status: 'loading' }
  | { status: 'error';        message: string }
  | { status: 'no-shops' }
  | { status: 'needs-picker'; shops: Shop[] }
  | { status: 'ready';        shop:  Shop; shops: Shop[] }

interface ShopContextValue {
  state:  ShopStatus
  select: (handle: string) => void
}

const ShopCtx = createContext<ShopContextValue | null>(null)

export function ShopProvider({ children }: { children: ReactNode }) {
  const whoami = useWhoAmI()
  const [state, setState] = useState<ShopStatus>({ status: 'loading' })

  const select = useCallback((handle: string) => {
    setState(prev => {
      if (prev.status !== 'needs-picker' && prev.status !== 'ready') return prev
      const shops = prev.shops
      const match = shops.find(s => s.handle === handle)
      if (!match) return prev
      localStorage.setItem(STORAGE_KEY, handle)
      return { status: 'ready', shop: match, shops }
    })
  }, [])

  useEffect(() => {
    if (whoami.state.status === 'loading') {
      setState({ status: 'loading' })
      return
    }
    if (whoami.state.status === 'error') {
      setState({ status: 'error', message: whoami.state.message })
      return
    }

    const shops = whoami.state.whoami.merchant.shops

    if (shops.length === 0) {
      setState({ status: 'no-shops' })
      return
    }

    // Restore previous selection if still in the shop list — common re-launch path.
    const persisted = localStorage.getItem(STORAGE_KEY)
    const preferred = persisted ? shops.find(s => s.handle === persisted) : undefined
    if (preferred) {
      setState({ status: 'ready', shop: preferred, shops })
      return
    }

    // Auto-select for single-shop merchants (majority case) — skip the picker.
    if (shops.length === 1) {
      const only = shops[0]!
      localStorage.setItem(STORAGE_KEY, only.handle)
      setState({ status: 'ready', shop: only, shops })
      return
    }

    // Multiple shops, no valid persisted selection — user picks.
    setState({ status: 'needs-picker', shops })
  }, [whoami.state])

  return <ShopCtx.Provider value={{ state, select }}>{children}</ShopCtx.Provider>
}

export function useShopContext(): ShopContextValue {
  const ctx = useContext(ShopCtx)
  if (!ctx) throw new Error('useShopContext must be used inside <ShopProvider>')
  return ctx
}

/**
 * Convenience for screens that only care about the currently-selected shop.
 * Returns null when the app hasn't finished loading a shop yet.
 */
export function useSelectedShop(): Shop | null {
  const { state } = useShopContext()
  return state.status === 'ready' ? state.shop : null
}
